import { Project } from '../../../../../../../models/Project';
import { ProjectExtensionType } from '../../../../../../../models/ProjectExtension';
import {
  PromanDocument,
  PromanReport,
  PromanReportCollection,
} from '../../../../../api/promanDtos';

/**
 * Map documents by reportID and reportCollectionID using provided IDs
 * Key: `${reportCollectionID}_${reportID}`
 */
export const mapDocumentsByReport = (
  documents: PromanDocument[],
  collections: PromanReportCollection[]
): Record<string, PromanDocument[]> => {
  const mapped: Record<string, PromanDocument[]> = {};

  documents.forEach((doc) => {
    if (
      typeof doc.reportCollectionID === 'number' &&
      typeof doc.reportID === 'number'
    ) {
      const key = `${doc.reportCollectionID}_${String(doc.reportID).padStart(
        3,
        '0'
      )}`;
      if (!mapped[key]) mapped[key] = [];
      mapped[key].push(doc);
    }
  });

  Object.keys(mapped).forEach((key) => {
    mapped[key].sort(
      (a, b) =>
        new Date(b.lastModifiedDate).getTime() -
        new Date(a.lastModifiedDate).getTime()
    );
  });

  return mapped;
};

/**
 * Get project extension by type
 */
export const getProjectExtensionMe = (
  project: Project,
  type: ProjectExtensionType
) => {
  if (!project) {
    return undefined;
  }
  return project.projectExtensionMeDtos?.find(
    (ext) => ext.projectExtensionType === type
  );
};

/**
 * Format date string to local date format
 */
export const formatDate = (dateString: string): string => {
  try {
    return new Date(dateString).toLocaleDateString();
  } catch {
    return dateString;
  }
};

const queryInReport = (query: string, report: PromanReport): boolean => {
  const lowerQuery = query.toLowerCase().replace(/\W/g, '');
  return (
    report.reportCode.toLowerCase().includes(lowerQuery) ||
    report.reportId.toString().includes(lowerQuery) ||
    (report.meetingDate &&
      formatDate(report.meetingDate).toLowerCase().includes(lowerQuery)) ||
    (report.meetingPlace &&
      report.meetingPlace.toLowerCase().includes(lowerQuery))
  );
};

/**
 * Filter reports by search query
 */
export const filterReports = (
  reports: PromanReport[],
  searchQuery: string
): PromanReport[] => {
  if (!searchQuery.trim()) {
    return reports;
  }

  const queries = searchQuery
    .toLowerCase()
    .split(' ')
    .filter((q) => q);
  return reports.filter((report) =>
    queries.every((query) => queryInReport(query, report))
  );
};

/**
 * Sort reports by reportId descending (newest first)
 */
export const sortReports = (reports: PromanReport[]): PromanReport[] => {
  return [...reports].sort((a, b) => b.reportId - a.reportId);
};

/**
 * Get documents for a specific report
 */
export const getReportDocuments = (
  report: PromanReport,
  organizedDocuments: Record<string, PromanDocument[]>
): PromanDocument[] => {
  const reportNumber = String(report.reportId).padStart(3, '0');
  const key = `${report.reportCollectionID}_${reportNumber}`;
  return organizedDocuments[key] || [];
};

/**
 * Check if report has documents
 */
export const hasReportDocuments = (
  report: PromanReport,
  organizedDocuments: Record<string, PromanDocument[]>
): boolean => {
  if (!report || !report.reportCollectionID || !report.reportCode) {
    return false;
  }
  return getReportDocuments(report, organizedDocuments).length > 0;
};

/**
 * Group collections by folderID
 */
export const groupCollectionsByFolder = (
  collections: PromanReportCollection[]
): Record<number, PromanReportCollection[]> => {
  return collections.reduce(
    (acc, collection) => {
      const folderId = collection.folderID;
      if (!acc[folderId]) {
        acc[folderId] = [];
      }
      acc[folderId].push(collection);
      return acc;
    },
    {} as Record<number, PromanReportCollection[]>
  );
};

/**
 * Check if a collection has reports matching the search query
 */
export const collectionHasMatchingReports = (
  collectionId: number,
  reportsByCollection: Record<number, PromanReport[]>,
  searchQuery: string,
  isLoadingAll: boolean = false
): boolean => {
  if (!searchQuery.trim()) return true;

  // While loading all reports, show all collections
  if (isLoadingAll) return true;

  const reports = reportsByCollection[collectionId];

  // If reports haven't been loaded yet during search, hide the collection
  if (!reports) return false;

  // If no reports exist in the collection, hide it
  if (reports.length === 0) return false;

  // If reports are loaded, check if any match the search
  const filtered = filterReports(reports, searchQuery);
  return filtered.length > 0;
};

/**
 * Check if a folder has collections with reports matching the search query
 */
export const folderHasMatchingReports = (
  collections: PromanReportCollection[],
  reportsByCollection: Record<number, PromanReport[]>,
  searchQuery: string,
  isLoadingAll: boolean = false
): boolean => {
  if (!searchQuery.trim()) return collections.length > 0;

  // While loading all reports, show all folders
  if (isLoadingAll) return collections.length > 0;

  return collections.some((collection) =>
    collectionHasMatchingReports(
      collection.reportCollectionId || 0,
      reportsByCollection,
      searchQuery,
      isLoadingAll
    )
  );
};

/**
 * Generate PROMAN external URLs
 */
export const generatePromanUrl = (
  mandateId: string | null,
  promanProjectId: number,
  folderGroups: Record<number, PromanReportCollection[]>,
  selectedReport?: PromanReport | null
): string => {
  const base = 'https://portal.proman.at/meetings';

  if (selectedReport) {
    const reportCollectionId = selectedReport.reportCollectionID;
    const reportId = selectedReport.reportId;
    const folderId = Object.keys(folderGroups).find((folderId) =>
      folderGroups[Number(folderId)].some(
        (collection) => collection.reportCollectionId === reportCollectionId
      )
    );

    if (reportCollectionId !== null && reportId !== null && folderId !== null) {
      return `${base}/${mandateId}/${promanProjectId}/${folderId}/${reportCollectionId}/${reportId}`;
    }
  }

  return `${base}/project/${mandateId}/${promanProjectId}`;
};
export const PROMAN_WEBCLIENT_URL =
  'https://pmhosting.proman.at/RDWeb/webclient/';
